import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useStaticQuery } from 'gatsby';
import {
  CATEGORY_SERVICES, // * enum of service categories applied to stores
  CATEGORY_FILTER_MODES,
} from '@lds/store-locator-js-sdk';
import useActiveStore from '../../../hooks/useActiveStore';
import useBullseyeLocations from '../../../hooks/store-locator/useBullseyeLocations';

// * This component uses the Bullseye API to return the five closest stores to a given lat & lng
// * The results are returned in the stores variable, and are ordered from closest to farthest
const WithBullseyeLocator = (Component) => {
  const BullseyeLocator = (props) => {
    const accountID = process.env.GATSBY_BULLSEYE_ACCOUNT_ID;
    const searchAPIKey = process.env.GATSBY_BULLSEYE_SEARCH_API_KEY;
    const { allContentfulStoreLogos } = useStaticQuery(query);

    const geocoder = useRef(null);
    const { activeStore, setActiveStore } = useActiveStore();
    const [locationLoaded, setLocationLoaded] = useState(false);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [findStore, setFindStore] = useState(false);
    const [storeToDisplay, setStoreToDisplay] = useState(null);
    const [showSelectBtn, setShowSelectBtn] = useState(false);
    const [userFilters, setUserFilters] = useState([]);
    const [placeName, setPlaceName] = useState();

    const intl = useIntl();

    // Retrieve active store from sessionStorage if available
    useEffect(() => {
      const store = window.sessionStorage.getItem('activeStore');

      if (!activeStore && store != null) {
        setActiveStore(JSON.parse(store));
        setFindStore(false);
      } else {
        setFindStore(true);
      }
    }, [activeStore, setActiveStore]);

    // * Create state to store a user's location
    const [location, setLocation] = useState({
      // Default loaction is Head Office at 600-500 Lakeshore Blvd
      lat: 43.637468,
      lng: -79.397889,
    });

    // Take ref to search bar and attatch the google autocomplete and geocoder classes to it
    const setInputs = useCallback(
      (inputNode) => handleSetInputs(inputNode, googleMapsLoaded, setLocation, setPlaceName),
      [googleMapsLoaded],
    );

    const searchByQuery = useCallback(
      (query) => handleSearchByQuery(query, geocoder, setLocation),
      [],
    );

    useEffect(() => {
      if (googleMapsLoaded && window.google.maps.Geocoder) {
        geocoder.current = new window.google.maps.Geocoder();
      }
    }, [googleMapsLoaded]);

    useEffect(() => {
      getCurrentLocation(setLocation, setLocationLoaded);
    }, []);

    useEffect(() => {
      if (window.google) {
        setGoogleMapsLoaded(true);
      } else if (!document.getElementById('google-maps-script')) {
        loadGoogleMapsScript(intl.locale, setGoogleMapsLoaded);
      }
    }, [intl.locale]);

    const options = {
      lang: intl.locale,
      clientId: accountID,
      apiKey: searchAPIKey,
      latitude: location.lat,
      longitude: location.lng,
      radius: 250,
      maxResults: 20,
      pageSize: 20,
      startIndex: 0,
    };

    const addOrRemoveEl = (arr, el) => {
      const newArr = [...arr];
      const index = newArr.indexOf(el);
      if (index > -1) {
        newArr.splice(index, 1);
      } else {
        newArr.push(el);
      }
      return newArr;
    };

    const toggleUserFilters = (filter) => {
      setUserFilters((state) => addOrRemoveEl(state, filter));
    };

    // * filters that should always be on
    // * this finds stores with the TMS service category
    const alwaysFilters = [CATEGORY_SERVICES.THE_MOBILE_SHOP];

    const searchOptions = {
      ...options,
      categoryLclIds: [...userFilters, ...alwaysFilters],
      filterMode: CATEGORY_FILTER_MODES.CAT_FILTER_AND,
    };

    // * when the search query loads, locations will contain a list of stores
    const { stores } = useBullseyeLocations(searchOptions);

    useEffect(() => {
      if (locationLoaded && stores?.length && findStore && !activeStore) {
        // * Bullseye sorts the stores by closest to farthest
        setActiveStore(stores[0]);
      }
    }, [stores, findStore, locationLoaded, setActiveStore, activeStore]);

    return (
      <Component
        intl={intl}
        origin={location}
        nearbyStores={stores}
        getCurrentLocation={() => getCurrentLocation(setLocation, setLocationLoaded)}
        allStoreLogos={allContentfulStoreLogos.nodes}
        isGoogleLoaded={googleMapsLoaded}
        setInputRef={setInputs}
        setStoreToDisplay={setStoreToDisplay}
        storeToDisplay={storeToDisplay}
        showSelectBtn={showSelectBtn}
        setShowSelectBtn={setShowSelectBtn}
        options={options}
        toggleUserFilters={toggleUserFilters}
        searchByQuery={searchByQuery}
        placeName={placeName}
        {...props}
      />
    );
  };
  return BullseyeLocator;
};

const handleSetInputs = (inputNode, googleMapsLoaded, setLocation, setPlaceName) => {
  if (!googleMapsLoaded || !inputNode) return;
  if (window.google.maps.places) {
    const googleAutoComplete = new window.google.maps.places.Autocomplete(inputNode, {
      types: ['geocode'],
      componentRestrictions: { country: 'ca' },
    });
    googleAutoComplete.addListener('place_changed', () => {
      const place = googleAutoComplete.getPlace();
      if (place.geometry) {
        setPlaceName(place.formatted_address);
        
        setLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
      } else if (place.name === '') {
        // If the search name is blanked out, return to the default location
        setLocation({
          lat: 43.637468,
          lng: -79.397889,
        });
      }
    });
  }
};

// eslint-disable-next-line
const handleSearchByQuery = (query, geocoder, setLocation) => new Promise((res, rej) => {
  if (!geocoder.current) {
    rej(new Error("Grocoder is not initialized."));
    return;
  }

  geocoder.current.geocode(
    { address: query, componentRestrictions: { country: 'CA' } },
    (results) => {
      if (results?.[0]?.geometry?.location) {
        const place = results[0];
        setLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        res(place);
      } else {
        rej(new Error("No valid geometry location found."));
      }
    },
  );
});

/**
 * Retrieves the current location of the user if geolocation is available.
 * This is necessary for providing location-based services such as showing nearby locations.
 * 
 * @param {function} setLocation - Callback to set the latitude and longitude.
 * @param {function} setLocationLoaded - Callback to indicate if location was successfully loaded.
 */
const getCurrentLocation = (setLocation, setLocationLoaded) => {
  if (!navigator.geolocation) {
    // Fallback to default location if geolocation is not available
    setLocation({
      lat: 43.637468,
      lng: -79.397889,
    });
    setLocationLoaded(false);
    return;
  }

  // geolocation is required to show the user the nearest stores based on their real-time location
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setLocationLoaded(true);
      setLocation({ lat, lng });
    },
    () => {
      // Handle location request denial or failure
      setLocation({
        lat: 43.637468,
        lng: -79.397889,
      });
      setLocationLoaded(false);
    },
  );
};

const loadGoogleMapsScript = (locale, setGoogleMapsLoaded) => {
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GATSBY_MAP_KEY}&language=${locale}&region=CA&libraries=places&loading=async`;
  script.async = true;
  script.id = "google-maps-script";
  script.onload = () => {
    setGoogleMapsLoaded(true);
  };
  document.body.appendChild(script);
};

const query = graphql`
  query allStoreLogos {
    allContentfulStoreLogos {
      nodes {
        storeName
        storeId
        logo {
          altText
          asset {
            url
            localImage {
              childImageSharp {
                gatsbyImageData
              }
              publicURL
            }
          }
        }
      }
    }
  }
`;

export default WithBullseyeLocator;
